import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { fetchItemExtra } from '../../redux/features/items/itemsSlice';
import {
  addUrlPost, refreshUrl, refreshUrlPosts, removeUrlPost, updateUrlPost,
} from '../../redux/features/urls/urlsThunks';

const extrasOf = (response) => (response.privateData || response.publicData)?.extras || [];

/**
 * The item's URL extras. Nothing loads until something calls `load`; a write
 * updates the entry it touched from its own answer.
 */
export default function useItemExtras(itemId, namespace) {
  const dispatch = useDispatch();

  const [extras, setExtras] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setStatus('loading');
    return dispatch(fetchItemExtra({ namespace, itemId }))
      .unwrap()
      .then((response) => {
        setExtras(extrasOf(response));
        setStatus('loaded');
      })
      .catch((e) => {
        setError(e.message);
        setStatus('failed');
      });
  }, [dispatch, itemId, namespace]);

  const setExtra = useCallback((urlId, update) => setExtras((stored) => stored.map(
    (entry) => (entry.urlId === urlId ? { ...entry, extra: update(entry.extra) } : entry),
  )), []);

  // Replaces one source's posts on a URL with what `update` makes of them.
  const setPosts = useCallback((urlId, source, update) => setExtra(urlId, (extra) => {
    const { posts, ...record } = extra[source] || {};
    const next = update(posts || {});
    return { ...extra, [source]: Object.keys(next).length ? { ...record, posts: next } : record };
  }), [setExtra]);

  const call = useCallback((thunk, args) => dispatch(thunk(args)).unwrap(), [dispatch]);

  const refresh = useCallback((urlId, ai) => call(refreshUrl, { urlId, ai })
    .then(({ extra }) => setExtra(urlId, () => extra)), [call, setExtra]);

  const addPost = useCallback((urlId, post) => call(addUrlPost, { urlId, post })
    .then(({
      source, id, post: added, failed,
    }) => {
      setPosts(urlId, source, (posts) => ({ ...posts, [id]: added }));
      return { source, id, failed };
    }), [call, setPosts]);

  // The answer's `id` is the post's new one when its link replaced it.
  const updatePost = useCallback((urlId, source, postId, patch) => call(updateUrlPost, {
    urlId, source, postId, patch,
  }).then(({ id, post }) => setPosts(
    urlId,
    source,
    ({ [postId]: replaced, ...posts }) => ({ ...posts, [id]: post }),
  )), [call, setPosts]);

  const removePost = useCallback((urlId, source, postId) => call(removeUrlPost, {
    urlId, source, postId,
  }).then(() => setPosts(
    urlId,
    source,
    ({ [postId]: removed, ...posts }) => posts,
  )), [call, setPosts]);

  const refreshPosts = useCallback((urlId, source, postId) => call(refreshUrlPosts, {
    urlId, source, postId,
  }).then((data) => {
    setPosts(urlId, source, (posts) => ({ ...posts, ...data.posts }));
    return data;
  }), [call, setPosts]);

  return {
    extras, status, error, load, refresh, addPost, updatePost, removePost, refreshPosts,
  };
}
